import { type DisciplineSlug } from "@jumpbean/shared";

export interface DisciplineProgressRingProps {
  discipline: DisciplineSlug;
  disciplineName: string;
  progress: number; // 0-100
  starsEarned: number;
  maxStars: number;
  size?: "sm" | "md" | "lg";
  showLabel?: boolean;
}

/**
 * Circular progress ring for displaying discipline progress.
 * Features animated ring with discipline-specific colors.
 */
export function DisciplineProgressRing({
  discipline,
  disciplineName,
  progress,
  starsEarned,
  maxStars,
  size = "md",
  showLabel = true,
}: DisciplineProgressRingProps) {
  // Size configurations
  const sizeConfig = {
    sm: { container: 64, stroke: 4, radius: 26, fontSize: "text-xs", iconSize: 20 },
    md: { container: 88, stroke: 5, radius: 36, fontSize: "text-sm", iconSize: 28 },
    lg: { container: 120, stroke: 6, radius: 50, fontSize: "text-base", iconSize: 40 },
  };

  const config = sizeConfig[size];
  const circumference = 2 * Math.PI * config.radius;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  // Discipline color configurations
  const disciplineColors: Record<DisciplineSlug, {
    gradient: string;
    bg: string;
    text: string;
    track: string;
    emoji: string;
  }> = {
    math: {
      gradient: "url(#math-gradient)",
      bg: "bg-gradient-to-br from-luna-light/30 to-magic-200/30",
      text: "text-magic-600",
      track: "stroke-magic-100",
      emoji: "🦄",
    },
    spelling: {
      gradient: "url(#spelling-gradient)",
      bg: "bg-gradient-to-br from-petal-light/30 to-meadow-200/30",
      text: "text-meadow-600",
      track: "stroke-meadow-100",
      emoji: "🧚",
    },
    patterns: {
      gradient: "url(#patterns-gradient)",
      bg: "bg-gradient-to-br from-fairy-100/30 to-sky-100/30",
      text: "text-prism-red",
      track: "stroke-fairy-100",
      emoji: "🐉",
    },
    typing: {
      gradient: "url(#typing-gradient)",
      bg: "bg-gradient-to-br from-whiskers-light/30 to-sky-200/30",
      text: "text-sky-600",
      track: "stroke-sky-100",
      emoji: "🐱",
    },
    programming: {
      gradient: "url(#programming-gradient)",
      bg: "bg-gradient-to-br from-pixel-light/30 to-[#FFE4C4]/30",
      text: "text-pixel",
      track: "stroke-[#FFE4C4]",
      emoji: "🦉",
    },
  };

  const colors = disciplineColors[discipline] ?? disciplineColors.math;

  return (
    <div className="flex flex-col items-center gap-2">
      <div
        className={`relative ${colors.bg} rounded-full p-1`}
        style={{ width: config.container, height: config.container }}
      >
        <svg
          width={config.container}
          height={config.container}
          className="transform -rotate-90"
        >
          {/* Gradient definitions */}
          <defs>
            <linearGradient id="math-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#E8A0FF" />
              <stop offset="100%" stopColor="#9B59B6" />
            </linearGradient>
            <linearGradient id="spelling-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#90EE90" />
              <stop offset="100%" stopColor="#4CAF50" />
            </linearGradient>
            <linearGradient id="patterns-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#FF6B6B" />
              <stop offset="50%" stopColor="#FFE66D" />
              <stop offset="100%" stopColor="#74B9FF" />
            </linearGradient>
            <linearGradient id="typing-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#4A90D9" />
              <stop offset="100%" stopColor="#2563EB" />
            </linearGradient>
            <linearGradient id="programming-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#FF8C42" />
              <stop offset="100%" stopColor="#E67300" />
            </linearGradient>
          </defs>

          {/* Background track */}
          <circle
            cx={config.container / 2}
            cy={config.container / 2}
            r={config.radius}
            fill="none"
            strokeWidth={config.stroke}
            className={colors.track}
          />

          {/* Progress arc */}
          <circle
            cx={config.container / 2}
            cy={config.container / 2}
            r={config.radius}
            fill="none"
            strokeWidth={config.stroke}
            stroke={colors.gradient}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            className="transition-all duration-700 ease-out"
          />
        </svg>

        {/* Center content */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span style={{ fontSize: config.iconSize }} role="img" aria-hidden="true">
            {colors.emoji}
          </span>
        </div>
      </div>

      {showLabel && (
        <div className="text-center">
          <p className={`font-display font-semibold ${config.fontSize} ${colors.text}`}>
            {disciplineName}
          </p>
          <p className="font-body text-xs text-neutral-500">
            {progress}% complete
          </p>
        </div>
      )}
    </div>
  );
}

export default DisciplineProgressRing;
